import { Component, OnInit, Input, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { AgentErrorCode, FileManagerService } from 'xsmodules/remote-control/services/file-manager.service';
import { Subscription } from 'rxjs';
import { RemoteControlDeviceState } from 'app/modules/remote-control/services/device-state-registry.service';
import { FileData } from 'xsmodules/remote-control/file-manager/fileData';
import { hd } from '@nx-fe/services';
import { XsI18nPipe } from '@nx-fe/pipes';
import { RcContext } from 'xsmodules/remote-control-ng/context/rcContext';

@Component({
  selector: 'app-file-manager-rename-item',
  templateUrl: './file-manager-rename-item.component.html',
  styleUrls: ['./file-manager-rename-item.component.scss'],
})
export class FileManagerRenameItemComponent implements OnInit, OnDestroy {
  public inputStateActive = true;
  public formDetails: FormGroup;
  public errorMessage = '';
  public serverErrorMessage: string;
  private sessionEndedSubscription: Subscription;
  private originalFileName = '';
  public itemType: string;

  public fileSaveParams: hd.FileSaveParams = {
    fileName: null,
    extension: null,
  };

  @Input() dialogParams: hd.PromptDialogParams;
  @Input() rcState: RemoteControlDeviceState;

  constructor(
    private fileManagerService: FileManagerService,
    private modalDialog: MatDialogRef<FileManagerRenameItemComponent>,
    private saI18nPipe: XsI18nPipe,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.loadForm();
    this.sessionEndedSubscription = this.fileManagerService.sessionEnded.subscribe(() => {
      this.onCancel();
    });
  }

  ngOnDestroy() {
    if (this.sessionEndedSubscription) {
      this.sessionEndedSubscription.unsubscribe();
    }
  }

  public loadForm() {
    this.fileSaveParams.fileName = this.dialogParams.placeHolder ? this.dialogParams.placeHolder : null;
    this.fileSaveParams.extension = this.dialogParams.extension ? this.dialogParams.extension : null;
    this.itemType = this.fileManagerService.getCurrentItemType() as string;

    this.formDetails = this.fb.group({
      fileName: [this.fileSaveParams.fileName, [Validators.required, Validators.maxLength(200)]],
    });

    this.originalFileName = this.fileSaveParams.fileName;
  }

  public get fileName(): AbstractControl {
    return this.formDetails.get('fileName');
  }

  /** Return the error message is exists */
  public getErrorMessage = (): string => this.errorMessage || '';

  public getServerErrorMessage = (): string => this.serverErrorMessage || '';

  /** Cancels the modal dialog */
  public onCancel = (): void => {
    this.modalDialog.close('cancel');
  };

  public setFileName = (fileName: string): void => {
    if (!fileName || !fileName.replace(/\s/g, '').length) {
      this.errorMessage = this.saI18nPipe.transform('prompt_dialog_Error_InvalidInput');
      return;
    }
    this.fileSaveParams.fileName = fileName;
    const myArray = this.fileSaveParams.fileName.match(this.dialogParams.regexFilter);
    if (myArray) {
      this.errorMessage = this.saI18nPipe.transform('prompt_dialog_input_format', this.dialogParams.unallowedChar);
    } else {
      this.errorMessage = null;
    }
  };

  public isFileNameValid = (): boolean => !this.errorMessage;

  public isExtensionModified = (): boolean => {
    const oldExtension = this.getFileExtension(this.originalFileName);
    const newExtension = this.getFileExtension(this.fileName.value);

    return oldExtension !== newExtension && this.itemType === 'file';
  };

  public isDisabled = (): boolean =>
    this.fileName.value.length < 1 ||
    this.fileName.value.trim() === '' ||
    this.originalFileName === this.fileName.value.trim();

  public onRename = (): void => {
    this.setFileName(this.fileName.value);
    if (!this.errorMessage && this.fileSaveParams.fileName && this.fileSaveParams.fileName.trim().length > 0) {
      const selectedItem = this.fileManagerService.getCurrentItem() as FileData;
      const folderNameLength = selectedItem.fullPath.length - selectedItem.fileName.length;
      const folderName = selectedItem.fullPath.substring(0, folderNameLength);
      const oldFullFileName = selectedItem.fullPath;
      const newFullFileName = folderName + this.fileName.value;
      this.fileManagerService.isFileExistsOnRemoteFolder(folderName, this.fileName.value).subscribe(result => {
        if (result) {
          this.errorMessage = this.saI18nPipe.transform('Rename_New_Name_Already_In_Use');
          console.log(
            `File Rename ${oldFullFileName} failed. fileExists: ${result}, ErrorDescription: "New name already in use."`
          );
        } else {
          this.fileManagerService
            .renameItem(oldFullFileName, newFullFileName, this.fileName.value)
            .subscribe(result => {
              let serverErrorMessage = '';
              switch (result) {
                case AgentErrorCode.Success:
                  console.log('File Rename complete' + oldFullFileName + ' =>  ' + newFullFileName);
                  break;

                case AgentErrorCode.Access_Denied:
                  serverErrorMessage = 'Insufficient permissions.';
                  break;

                case AgentErrorCode.Not_Exists:
                  serverErrorMessage = 'Item does not exist.';
                  break;

                case AgentErrorCode.Already_Exists:
                  serverErrorMessage = 'New name already in use.';
                  break;

                default:
                  serverErrorMessage = 'Undefined Error ' + result;
              }

              this.errorMessage = serverErrorMessage;

              if (serverErrorMessage) {
                console.log(
                  'File Rename ' +
                    oldFullFileName +
                    ' failed. ErrorCode:' +
                    result +
                    ', ErrorDescription: ' +
                    serverErrorMessage
                );
              }

              if (!result) {
                this.modalDialog.close(this.fileName.value);

                /** search again after renaming the file */
                const state = this.fileManagerService.getState(RcContext.instance.sotiAssistDeviceId);
                if (state) {
                  state.clearPreviousSearchResults();
                  state.onSearchButton();
                }
              }
            });
        }
      });
    }
  };

  private getFileExtension = (filename: string): string => filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
}
